/**
 * BottomSheet — iOS-native style modal that slides up from the bottom on
 * mobile and renders as a centered card on desktop. Used by the Capture
 * §4.5 chip-edit and Swap pickers.
 *
 * Behavior:
 *   - Backdrop click → onClose
 *   - Escape key → onClose
 *   - Body scroll locked while open
 *   - Auto-focuses first focusable child on open (caller can prevent by
 *     adding tabIndex={-1} to the relevant nodes)
 */
import { useEffect, useRef } from 'preact/hooks';

interface BottomSheetProps {
  open: boolean;
  onClose: () => void;
  /** Optional header — `chipIdentity` style: a small eyebrow + serif title. */
  eyebrow?: string;
  title?: preact.ComponentChildren;
  children: preact.ComponentChildren;
}

export function BottomSheet({
  open,
  onClose,
  eyebrow,
  title,
  children,
}: BottomSheetProps) {
  const sheetRef = useRef<HTMLDivElement>(null);

  // Body scroll lock while sheet is open
  useEffect(() => {
    if (!open) return;
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = prevOverflow;
    };
  }, [open]);

  // Escape to close
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  // Focus first focusable child on open
  useEffect(() => {
    if (!open || !sheetRef.current) return;
    const focusable = sheetRef.current.querySelector<HTMLElement>(
      'input, [tabindex]:not([tabindex="-1"]), button:not([tabindex="-1"])',
    );
    focusable?.focus();
  }, [open]);

  if (!open) return null;

  return (
    <div
      class="bottom-sheet-backdrop"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div class="bottom-sheet" ref={sheetRef} role="dialog" aria-modal="true">
        <div class="bottom-sheet-grabber" aria-hidden="true" />
        {(eyebrow || title) && (
          <div class="bottom-sheet-head">
            {eyebrow && <div class="bottom-sheet-eyebrow">{eyebrow}</div>}
            {title && <div class="bottom-sheet-title">{title}</div>}
          </div>
        )}
        <div class="bottom-sheet-body">{children}</div>
      </div>
    </div>
  );
}
